import * as preact from "preact";

import * as keyboardShortcuts from "keyboardShortcuts";
import * as utils from "shared/utils";

export type SyncAudioClickMode = "translate" | "sync" | "goto" | "disabled";

export interface SyncAudioControlsProps {
  initiallyVisible: boolean;
  mediaPanelHeight?: number;
  onClickModeChange: (clickMode: SyncAudioClickMode) => void;
  onMediaPanelHeightChange?: (height: number) => void;
  owner: boolean;
  setResizing?: (resizing: boolean) => void;
  showControlsKey?: number;
}

interface SyncAudioControlsState {
  clickMode: SyncAudioClickMode;
  hintMode?: SyncAudioClickMode;
  hintVisible: boolean;
  visible: boolean;
}

const CLICK_MODE_LABELS: Record<SyncAudioClickMode, string> = {
  disabled: "Disable Sync",
  goto: "Play",
  sync: "Sync",
  translate: "Translate",
};

const CLICK_MODE_HINTS: Record<SyncAudioClickMode, preact.ComponentChildren> = {
  disabled: (
    <>
      <strong>Disable Sync Mode:</strong> click on a word below to translate it
      (with audio sync off)
    </>
  ),
  goto: (
    <>
      <strong>Play Mode:</strong> click on a word below to start playing from
      that point
    </>
  ),
  sync: (
    <>
      <strong>Sync Mode:</strong> click on a word below exactly when you hear it
      to synchronize it with the audio
    </>
  ),
  translate: (
    <>
      <strong>Translate Mode:</strong> click on a word below to translate it
      (with audio sync on)
    </>
  ),
};

export default class SyncAudioControls extends preact.Component<
  SyncAudioControlsProps,
  SyncAudioControlsState
> {
  private fadeHintTimeout?: number;
  private resizing = false;
  private resizeDragStartY?: number;
  private resizeOriginalHeight?: number;

  constructor(props: SyncAudioControlsProps) {
    super(props);
    this.state = {
      clickMode: "translate",
      hintMode: "translate",
      hintVisible: false,
      visible: props.initiallyVisible,
    };
  }

  componentDidMount() {
    this.props.onClickModeChange(this.state.clickMode);
    this.registerKeyboardShortcuts();
  }

  componentDidUpdate(previousProps: SyncAudioControlsProps) {
    if (
      this.props.showControlsKey !== undefined &&
      this.props.showControlsKey !== previousProps.showControlsKey
    ) {
      this.showControls();
    }
  }

  componentWillUnmount() {
    this.unregisterKeyboardShortcuts();
    this.stopResize();
    if (this.fadeHintTimeout) {
      clearTimeout(this.fadeHintTimeout);
    }
  }

  showControls() {
    this.setState({ visible: true });
  }

  private registerKeyboardShortcuts() {
    keyboardShortcuts.registerHandler("readerPage", "x".charCodeAt(0), () => {
      this.selectClickMode("translate");
    });
    keyboardShortcuts.registerHandler("readerPage", "c".charCodeAt(0), () => {
      if (this.props.owner) {
        this.selectClickMode("sync");
      }
    });
    keyboardShortcuts.registerHandler("readerPage", "v".charCodeAt(0), () => {
      this.selectClickMode("goto");
    });
  }

  private unregisterKeyboardShortcuts() {
    keyboardShortcuts.unregisterHandler("readerPage", "x".charCodeAt(0));
    keyboardShortcuts.unregisterHandler("readerPage", "c".charCodeAt(0));
    keyboardShortcuts.unregisterHandler("readerPage", "v".charCodeAt(0));
  }

  private selectClickMode = (clickMode: SyncAudioClickMode) => {
    this.setState(
      {
        clickMode,
        hintMode: clickMode,
        hintVisible: true,
      },
      () => {
        this.props.onClickModeChange(clickMode);
        this.startFadeOutTimer();
      }
    );
  };

  private startResize = (event: MouseEvent) => {
    event.preventDefault();
    this.resizeDragStartY = event.pageY;
    this.resizeOriginalHeight = this.props.mediaPanelHeight ?? 0;
    this.resizing = true;
    this.props.setResizing?.(true);
    document.addEventListener("mousemove", this.handleResizeMove);
    document.addEventListener("mouseup", this.stopResize);
  };

  private handleResizeMove = (event: MouseEvent) => {
    if (
      this.resizeDragStartY === undefined ||
      this.resizeOriginalHeight === undefined
    ) {
      return;
    }

    this.props.onMediaPanelHeightChange?.(
      this.resizeOriginalHeight + event.pageY - this.resizeDragStartY
    );
  };

  private stopResize = () => {
    const wasResizing = this.resizing;
    this.resizing = false;
    this.resizeDragStartY = undefined;
    this.resizeOriginalHeight = undefined;
    if (wasResizing) {
      this.props.setResizing?.(false);
    }
    document.removeEventListener("mousemove", this.handleResizeMove);
    document.removeEventListener("mouseup", this.stopResize);
  };

  private showClickModeHint = (clickMode: SyncAudioClickMode) => {
    if (this.fadeHintTimeout) {
      clearTimeout(this.fadeHintTimeout);
    }
    this.setState({
      hintMode: clickMode,
      hintVisible: true,
    });
  };

  private startFadeOutTimer = (selectedHint?: SyncAudioClickMode) => {
    if (this.fadeHintTimeout) {
      clearTimeout(this.fadeHintTimeout);
    }

    if (selectedHint && this.state.clickMode !== selectedHint) {
      this.setState({ hintVisible: false });
      return;
    }

    this.fadeHintTimeout = window.setTimeout(() => {
      this.setState({ hintVisible: false });
    }, 4500);
  };

  private renderClickModeInput(clickMode: SyncAudioClickMode, title?: string) {
    const inputID = `toggle-${clickMode}`;
    return (
      <>
        <input
          type="radio"
          name="clickAction"
          id={inputID}
          value={clickMode}
          checked={this.state.clickMode === clickMode}
          onClick={() => this.selectClickMode(clickMode)}
          onChange={() => this.selectClickMode(clickMode)}
        />
        <label
          for={inputID}
          class={this.state.clickMode === clickMode ? "selected" : undefined}
          title={title}
          onMouseEnter={() => this.showClickModeHint(clickMode)}
          onMouseLeave={() => this.startFadeOutTimer(clickMode)}
        >
          {CLICK_MODE_LABELS[clickMode]}
        </label>
      </>
    );
  }

  render() {
    const hintMode = this.state.hintMode ?? this.state.clickMode;

    return (
      <>
        <div
          class={utils.cn({
            clickActionToggles: true,
            syncAudioControlsHidden: !this.state.visible,
          })}
        >
          {this.renderClickModeInput(
            "translate",
            "Translate Mode (keyboard shortcut: x)"
          )}
          {this.props.owner
            ? this.renderClickModeInput(
                "sync",
                "Sync Mode (keyboard shortcut: c)"
              )
            : null}
          {this.renderClickModeInput(
            "goto",
            "Play Mode (keyboard shortcut: v)"
          )}
          {this.renderClickModeInput("disabled")}
          <div
            id="clickModeStatus"
            class={this.state.hintVisible ? "visible" : undefined}
          >
            {CLICK_MODE_HINTS[hintMode]}
          </div>
        </div>
        <div id="resizer" onMouseDown={this.startResize} />
      </>
    );
  }
}
